/**
 * DEMO-ONLY: sessionStorage punchout users for My Account Punchout admin (MVP 1.3.1 / 2.5.x).
 * Removable with mock punchout fixtures / interceptor handlers.
 */
import { PunchoutType, PunchoutUser } from '../../punchout/models/punchout-user/punchout-user.model';

export const PRESIDIO_MOCK_PUNCHOUT_USERS_KEY = 'presidio-mock-punchout-users';

/** ICM channel prefix used in Link.uri values for resolveLinks. */
export const PRESIDIO_MOCK_PUNCHOUT_LINK_PREFIX = 'inSPIRED-inTRONICS_Business-Site/-';

export type PresidioMockPunchoutResource = 'cxml1.2' | 'oci5';

const DEFAULT_USERS: PunchoutUser[] = [
  {
    id: 'pat.punch@acme.com',
    login: 'pat.punch@acme.com',
    email: 'pat.punch@acme.com',
    active: true,
    punchoutType: 'cxml',
  },
  {
    id: 'oci.buyer',
    login: 'oci.buyer',
    email: 'oci.buyer@acme.demo',
    active: true,
    punchoutType: 'oci',
  },
];

function cloneUsers(users: PunchoutUser[]): PunchoutUser[] {
  return JSON.parse(JSON.stringify(users)) as PunchoutUser[];
}

function readAll(): PunchoutUser[] {
  if (typeof sessionStorage === 'undefined') {
    return cloneUsers(DEFAULT_USERS);
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_PUNCHOUT_USERS_KEY);
    if (raw === null) {
      writeAll(cloneUsers(DEFAULT_USERS));
      return cloneUsers(DEFAULT_USERS);
    }
    const parsed = JSON.parse(raw) as PunchoutUser[];
    return Array.isArray(parsed) ? parsed : cloneUsers(DEFAULT_USERS);
  } catch {
    return cloneUsers(DEFAULT_USERS);
  }
}

function writeAll(users: PunchoutUser[]): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_PUNCHOUT_USERS_KEY, JSON.stringify(users));
}

export function punchoutResourceForType(type: PunchoutType): PresidioMockPunchoutResource {
  return type === 'cxml' ? 'cxml1.2' : 'oci5';
}

export function punchoutTypeForResource(resource: string): PunchoutType | null {
  if (resource === 'cxml1.2') {
    return 'cxml';
  }
  if (resource === 'oci5') {
    return 'oci';
  }
  return null;
}

export function listPresidioMockPunchoutUsers(type: PunchoutType): PunchoutUser[] {
  return readAll().filter(u => u.punchoutType === type);
}

export function getPresidioMockPunchoutUser(type: PunchoutType, login: string): PunchoutUser | undefined {
  const decoded = decodeURIComponent(login);
  return readAll().find(
    u => u.punchoutType === type && (u.login === decoded || u.id === decoded || u.login?.toLowerCase() === decoded.toLowerCase())
  );
}

export function createPresidioMockPunchoutUser(user: Partial<PunchoutUser> & { login: string; punchoutType: PunchoutType }): PunchoutUser {
  const login = user.login.trim();
  const existing = getPresidioMockPunchoutUser(user.punchoutType, login);
  if (existing) {
    throw new Error('PUNCHOUT_USER_EXISTS');
  }
  const created: PunchoutUser = {
    id: login,
    login,
    email: user.email || `${login}@punchout.demo`,
    active: user.active !== false,
    punchoutType: user.punchoutType,
  };
  const all = readAll();
  all.push(created);
  writeAll(all);
  return created;
}

export function updatePresidioMockPunchoutUser(user: PunchoutUser): PunchoutUser {
  const all = readAll();
  const idx = all.findIndex(u => u.punchoutType === user.punchoutType && u.login === user.login);
  if (idx < 0) {
    throw new Error('PUNCHOUT_USER_NOT_FOUND');
  }
  const updated: PunchoutUser = {
    ...all[idx],
    active: user.active,
    email: user.email || all[idx].email,
    // password never stored/returned in demo
  };
  all[idx] = updated;
  writeAll(all);
  return { ...updated, password: undefined };
}

export function deletePresidioMockPunchoutUser(type: PunchoutType, login: string): void {
  const decoded = decodeURIComponent(login);
  const all = readAll();
  const next = all.filter(u => !(u.punchoutType === type && u.login === decoded));
  if (next.length === all.length) {
    throw new Error('PUNCHOUT_USER_NOT_FOUND');
  }
  writeAll(next);
}

export function buildPresidioMockPunchoutUserLink(customerNo: string, type: PunchoutType, login: string): {
  type: 'Link';
  uri: string;
} {
  const resource = punchoutResourceForType(type);
  return {
    type: 'Link',
    uri: `${PRESIDIO_MOCK_PUNCHOUT_LINK_PREFIX}/customers/${customerNo}/punchouts/${resource}/users/${encodeURIComponent(login)}`,
  };
}

export function buildPresidioMockPunchoutUsersEnvelope(customerNo: string, type: PunchoutType): { elements: { type: 'Link'; uri: string }[] } {
  return {
    elements: listPresidioMockPunchoutUsers(type).map(u =>
      buildPresidioMockPunchoutUserLink(customerNo, type, u.login || u.id)
    ),
  };
}

/** Reset to seeded defaults (tests). */
export function resetPresidioMockPunchoutUsers(): void {
  writeAll(cloneUsers(DEFAULT_USERS));
}
